import type { Dict } from './th';

export const en: Dict = {
  meta: { name: 'English', dateLocale: 'en-GB' },
  common: {
    next: 'Next', back: 'Back', save: 'Save', cancel: 'Cancel', close: 'Close', done: 'Done',
    skip: 'Skip', gotIt: 'Got it', loading: 'Loading…', retry: 'Try again',
    times: 'kicks', minutes: 'min', hours: 'h', week: 'Week', today: 'Today', yesterday: 'Yesterday',
    offline: "You're offline · saved on this phone, we'll sync when you're back online",
    saved: 'Saved', error: 'Something went wrong. Please try again.', demoBanner: 'Demo mode · data stays on this device only'
  },
  lang: { title: 'Choose your language', subtitle: 'เลือกภาษา', soon: 'Coming soon' },
  welcome: {
    title: 'Hello DinDin', titleLocal: '',
    lead: 'Log every kick — and see where your baby kicks, on a 3D bump that looks like yours.',
    start: 'Get started', haveAccount: 'I have an account. Sign in', trial: 'All features free for 7 days. No card needed.'
  },
  how: {
    title: 'Kick counting, made simple',
    p1t: 'Count to 10', p1: 'From week 28, doctors often suggest noticing 10 movements within 2 hours.',
    p2t: 'Tap any time', p2: 'Whenever you feel a kick, tap that spot. The app works out if you reached 10 within 2 hours.',
    p3t: 'Or sit down and focus', p3: 'Start a timed count and tap along.',
    note: "Hello DinDin helps you keep track — it doesn't replace your doctor. If movements feel different, you can always call your hospital.",
    consentPrivacy: 'I am 18 or older and accept the <a data-action="legal" data-doc="terms">Terms</a> and <a data-action="legal" data-doc="privacy">Privacy Policy</a>',
    consentHealth: "I agree to Hello DinDin storing my pregnancy and baby's movement data to show me my stats"
  },
  due: {
    title: "When's your due date?", lead: 'Used to work out how far along you are. You can change it later.', label: 'Due date',
    nowAt: "You're at", weeksDays: 'weeks, {d} days', daysLeft: '{n} days to go', pastDue: '{n} days past due date',
    early: "You're not at week 28 yet. Log whenever you like — daily counting starts at 28 weeks.",
    errTooFar: "That date looks too far away. Mind checking it?", errTooPast: 'That due date was a while ago. Mind checking it?'
  },
  names: {
    title: 'What should we call you?', mom: 'Your name', momPh: 'e.g. Mali',
    baby: "Baby's nickname (optional)", babyPh: 'e.g. Bean'
  },
  tryit: {
    title: 'Try tapping the bump', lead: 'Tap anywhere, just like when you feel a kick.',
    hint: 'Drag to turn and see the side', great: "That's it! 🎉", greatLead: "Whenever baby kicks, tap that spot. This was just practice — nothing saved."
  },
  start: {
    welcomeBack: 'Welcome back!',
    welcomeBackLead: 'Keep tapping, or tap "Keep using it" to save it',
    rTie: 'Even so far. A few more and a pattern starts to show.',
    step1: 'Tap the belly where you felt baby move',
    step1Sub: 'No sign-up, nothing to fill in. Just tap.',
    step2: 'What were you doing? One tap to note it',
    step2More: 'Write a longer note',
    step2Skip: 'Skip, show my report',
    step3: 'See where and when baby likes to move',
    step3Btn: 'See my report ›',
    title: 'Where did baby move? Tap right there',
    lead: 'A kick journal you can try right now. No app to download.',
    hint: 'Tap the belly where you felt baby move',
    count: '{n} taps · keep tapping any time',
    jotBubble: 'What were you doing just now? Try "Note"',
    seeReport: 'See report',
    continue: 'Keep using it',
    privacyLine: 'What you record stays on this device · we keep anonymous usage stats only · a journal, not a medical device',
    haveAccount: 'Have an account? Sign in',
    reportTitle: 'Your report',
    reportLead: 'From what you tapped and noted so far',
    rTaps: 'taps',
    rNotes: 'notes',
    rTagsTitle: 'What you were doing before baby moved',
    rTagsEmpty: 'No notes yet. Try "Note".',
    rNotesWritten: '{n} written notes',
    reportMore: 'The more days you record, the clearer where and when baby likes to move'
  },
  account: {
    title: 'Keep your data safe', lead: "Create an account so nothing is lost if you change phones — and see it on any device.",
    welcomeBack: 'Welcome back. Using the details saved in your account.', loginTitle: 'Welcome back', loginLead: 'Enter the email you signed up with.',
    guestNudge: 'Your notes are only on this phone right now.', guestNudgeCta: 'Keep them safe, free ›', guestCardLead: 'Everything you have recorded is only on this phone. Clearing the browser or changing phones would lose it. Make an account with your email and it all moves in.', guestCardCta: 'Save to an account (free)', savedToAccount: 'Saved to your account. You will see it on any device.',
    google: 'Continue with Google', or: 'or', email: 'Email', emailPh: 'you@example.com', sendCode: 'Email me a code',
    codeSent: 'We sent a 6-digit code to {email}', code: 'Code from the email', verify: 'Verify', resend: 'Send a new code',
    changeEmail: 'Use a different email', badEmail: "That email doesn't look right", badCode: 'That code is wrong or has expired',
    demoTitle: 'Demo mode', demoLead: "This version isn't connected to a server yet. Your data stays on this device.", demoContinue: 'Continue on this device'
  },
  hospital: {
    title: 'Your hospital phone number', lead: "Keep it in the app so you can call about baby's movements with one tap.",
    label: 'Phone (optional)', ph: 'e.g. 02 123 4567', invalid: 'Numbers, spaces and + - ( ) only', finish: 'Start using Hello DinDin'
  },
  home: {
    morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening',
    who: '{name}', whoBaby: '{name} & {baby}',
    idleTitle: 'Feel a kick? Tap the bump', idleLead: "We'll tell you when you reach 10 within 2 hours",
    countingTitle: '{n} of 10 kicks', countingLead: '{t} left in this 2-hour window',
    reachedTitle: '10 kicks reached', reachedLead: 'Took {m} minutes',
    todayDone: '10 reached today ✓', focus: 'Timed count', undo: 'Undo', undone: 'Last kick removed',
    todayCount: '{n} today', streak: '{n}-day streak', saveAccount: 'Create an account to keep your data',
    kickSaved: 'Saved · kick {n}'
  },
  coach: {
    s1t: 'Tap the bump when baby kicks', s1: 'Tap where you felt it. Drag to turn and see the side.',
    s2t: 'We count for you', s2: "This shows whether you've reached 10 within 2 hours. No timer needed.",
    s3t: 'Want to sit down and count?', s3: 'Start a 2-hour timed count and tap along.',
    step: '{i} / {n}'
  },
  session: {
    title: 'Timed count', tapHint: 'Tap where baby kicks', end: 'Finish', leave: 'Leave without saving?',
    leaveLead: "The {n} kicks in this count won't be kept", leaveOk: 'Leave', emptyEnd: "You haven't tapped yet. Leave?",
    note: 'Note (optional)', notePh: 'e.g. after dinner, very active',
    doneTitle: '10 kicks!', doneLead: 'In {m} minutes.',
    partialTitle: '{n} kicks logged', partialLead: 'Saved to your journal.',
    mins: 'min', mainZone: 'Main spot', backHome: 'Back to home'
  },
  help: {
    link: 'Baby moving less than usual?',
    title: 'You can always call',
    body: "If baby's movements feel less or different from usual, contact your doctor or maternity unit straight away. Don't wait for your next appointment.",
    callHospital: 'Call my hospital', addHospital: 'Add hospital number', emergency: 'Emergency: call 1669',
    disclaimer: 'This app is a journal. It does not check for or diagnose problems.'
  },
  zones: { upper_left: 'Upper left', upper_right: 'Upper right', lower_left: 'Lower left', lower_right: 'Lower right' },
  stats: {
    title: 'Stats', tabToday: 'Today', tabWeek: '7 days', tabZones: 'Where', tabHours: 'When',
    emptyTitle: 'No data yet', emptyLead: 'Tap the bump for the first time and your stats will show here',
    total: 'kicks in total', reached: '10 within 2 h', notYet: 'Not yet', reachedIn: 'Reached in {m} min',
    moreThan: '{n} more than yesterday', lessThan: '{n} fewer than yesterday', same: 'Same as yesterday',
    daily: 'Kicks per day', avg: 'Daily average', daysReached: 'Days reaching 10',
    whereTitle: 'Where baby kicks most', top: 'Most often: {z} ({p}%)', noZone: 'No location data yet',
    zoneNote: "Where kicks are felt can help conversations with your doctor, but it doesn't tell baby's position.",
    hoursTitle: "When baby's most active", peak: 'Most active around {h}', sessions: 'Timed counts',
    locked: 'Premium feature', unlock: 'See Premium'
  },
  profile: {
    title: 'Profile', pregnancy: 'Pregnancy', dueDate: 'Due date', mom: 'Your name', baby: "Baby's nickname", notSet: 'Not set',
    hospital: 'Hospital phone', app: 'App', language: 'Language', sound: 'Tap sounds',
    plan: 'Plan', trialLeft: 'Trial · {n} days left', premiumUntil: 'Premium until {d}', premiumForever: 'Premium · unlocked forever', free: 'Free', upgrade: 'Upgrade to Premium',
    account: 'Account', signedInAs: 'Signed in as', signOut: 'Sign out', signOutLead: "Your data stays in your account. Sign in any time to see it.",
    data: 'My data', export: 'Download all my data', delete: 'Delete account and data',
    deleteLead: 'All pregnancy and kick data will be permanently deleted. This cannot be undone.', deleteOk: 'Delete permanently', deleted: 'Your data has been deleted',
    about: 'About', terms: 'Terms of use', privacy: 'Privacy policy', notMedical: 'Hello DinDin is not a medical device',
    demoTools: 'Test tools (demo mode only)', seed: 'Add 7 days of sample data', expire: 'Simulate: trial ended', version: 'Version {v}'
  },
  paywall: {
    title: 'Unlock everything', trialLead: "You're using every feature free — {n} days left",
    endedTitle: 'Your 7-day trial has ended', endedLead: 'Kick counting stays free forever. Upgrade to see where and when baby kicks.',
    f1: 'Record where baby kicks on the 3D bump', f2: 'History, kick map and baby’s active hours', f3: 'Kick counting, journal and a call-your-hospital button, free forever', f4: 'DinDin Wrapped keepsake to share (coming soon)',
    lifetime: 'Unlock forever', lifetimeSub: 'Pay once, yours for good on this account',
    noAuto: 'No subscription, never charged again · PromptPay or card',
    buy: 'Pay now', later: 'Maybe later', continueFree: 'Continue with free', soon: 'Payments open soon', signInToBuy: 'Make an account to buy (your notes come with you)',
    thanks: 'Thank you! Premium is on.', pending: 'Confirming your payment…',
    heroCount: '{baby} has said hello {n} times', heroDefault: 'Keep every hello from your baby', heroSub: 'Want to see where and when {baby} likes to kick?', babyFallback: 'Baby',
    favSpot: 'Favourite spot', favHour: 'Most active', hourFmt: '{h}:00', sample: 'Sample', soonChip: 'Soon',
    cardOwl: 'Baby\'s kick personality', cardBook: 'Birth keepsake book', cardFilm: 'Your baby\'s story film', cardTimelapse: 'Your bump, week by week',
    trySamples: 'Tap to see what you get', sampleNote: 'Made with sample data. Yours is made from your own baby\'s hellos.',
    pOwl: 'Little owl', pOwlQ: '“I like to kick at 9pm, Mum”', pRooster: 'Early bird', pRoosterQ: '“Up before you every day”', pCat: 'Afternoon kitty', pCatQ: '“I kick after your lunch”',
    colFree: 'Free', colPremium: 'Premium', rowCount: 'Counting, journal, special moments', rowCall: 'Call-your-hospital button',
    rowSpot: 'Where baby kicks on the 3D bump', rowStats: 'Stats + baby\'s active hours', rowWrapped: 'Keepsake book + story film',
    letterTitle: 'From the maker', letter: '', letterSign: '— Dew', seeAll: 'See everything you get', yours: 'Your Premium'
  },
  moments: {
    section: 'Special moments', notSet: 'Not yet', saved: 'Saved', savedWeek: 'Week {n}',
    first_kick: 'First hello from baby', partner_felt: 'First time someone else felt baby move', last_before_birth: 'Last hello before we met',
    lead_first_kick: 'Which week was it, and where on your bump? Whatever you remember is enough.',
    lead_partner_felt: 'The day someone put a hand on your bump and felt baby move for the first time.',
    lead_last_before_birth: 'The last kick inside, before the day you met.',
    note_first_kick: 'e.g. we were watching a film together', note_partner_felt: 'e.g. he jumped, then laughed', note_last_before_birth: 'e.g. the night before the hospital',
    week: 'Week', weekN: 'Week {n}', weekUnknown: "Don't remember", spot: 'Where on your bump (optional)',
    spotHint: 'Tap the spot you remember', spotSet: 'Tap again to move it',
    save: 'Keep in the book', savedToast: 'Kept in your book 💛', remove: 'Remove this moment', removed: 'Removed'
  },
  journal: {
    button: 'Note', title: 'What were you doing before baby moved?', lead: "No need to note every kick, just when you notice something. Pick as many as you like.",
    nudge: 'Baby moved! What were you doing? Tap to note it ✏️', firstSaved: 'Saved! The more you note, the better your birth keepsake knows your little one 💛',
    noteLabel: 'Add a note (optional)', notePh: 'e.g. Dad sang and baby kicked hard',
    save: 'Save', saved: 'Saved 💛', recent: 'Recent notes', empty: 'Nothing noted yet',
    delete: 'Delete', deleted: 'Deleted'
  },
  tags: {
    after_meal: 'After a meal', sweet_cold: 'Sweet or cold', music: 'Music', voice: 'Talking to baby',
    resting: 'Resting', bedtime: 'Bedtime', walking: 'Walking', bath: 'Bath'
  },
  nav: { home: 'Home', stats: 'Stats', profile: 'Profile' },
  legal: {
    draft: 'Draft · to be reviewed by a legal professional before launch',
    terms: 'Hello DinDin is a tool for logging fetal movements. It is not a medical device and does not provide diagnosis. Users must be 18 or older. Premium is a one-time payment that unlocks it permanently on the account that bought it — no subscription. If the service ever has to close, we will give notice and let you download all your data.',
    privacy: "We collect only what we need: your email, the name you choose, due date, hospital phone (if added), the time/location of kicks you log, and what you jot down (tags and notes). Data is stored on servers in Singapore and encrypted in transit. We never sell or share data for advertising. You can download or delete everything yourself from the Profile page. We also keep anonymous usage stats to improve the app: which steps or buttons were used, which screens were opened, the channel you came from (e.g. TikTok) and a random device id. They never include when or where baby moved, what you wrote, your name or your due date."
  }
};
